import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import {
  ArrowLeft,
  Bell,
  PaperPlaneTilt,
  ArrowsClockwise,
  CheckCircle,
  Warning,
  Database,
  Cloud,
} from "@phosphor-icons/react";

/**
 * /admin/notification-preferences  (admin/manager only)
 *
 * Admin UI for the alerting-config loop:
 *   • OPS_ALERT_EMAILS  (recipient list for ops emails)
 *   • ALERT_FAILED_CREDS_PER_HOUR  (rolling-hour brute-force threshold)
 *   • ALERT_LOCKOUTS              (account lockout count threshold)
 *   • ALERT_HIBP_REJECTED         (breached-password attempt threshold)
 *   • ALERT_ACK_TTL_HOURS         (how long an acknowledged alert stays silenced)
 *   • ALERT_DEDUPE_HOURS          (max 1 ops email per rule per this window)
 *
 * Per-field source badge — shows whether the effective value comes from
 * the DB override or the env-var fallback. Save with empty list to revert
 * emails to env. Save 0 / blank to revert numeric fields to env (handled
 * server-side by validation + $unset on partial empty payloads).
 *
 * Companion to the just-shipped Acknowledge Anomaly flow — both surfaces
 * read from the same notification_preferences DB row, so changes here
 * propagate to the digest banner, the email worker, AND the ack-TTL clock.
 */

function SourceBadge({ source }) {
  // Bias toward visibility: when a value comes from the DB, show a small
  // emerald badge to make it obvious which fields ops has explicitly
  // configured vs which are still defaults. Encourages "config-as-data"
  // discipline.
  if (source === "db") {
    return (
      <span
        className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider bg-emerald-100 text-emerald-800 border border-emerald-300"
        title="Value persisted in notification_preferences DB row"
        data-testid="source-db"
      >
        <Database size={9} weight="fill" />
        DB
      </span>
    );
  }
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider bg-[#FBF9F6] text-[#4A5C50] border border-[#E5E0D8]"
      title="Value comes from env-var fallback (no DB override yet)"
      data-testid="source-env"
    >
      <Cloud size={9} weight="fill" />
      ENV
    </span>
  );
}

export default function NotificationPreferences() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Local editable copy. Numeric fields use string state so user can clear
  // them (empty input = revert-to-env on save).
  const [emails, setEmails] = useState("");
  const [failedCreds, setFailedCreds] = useState("");
  const [lockouts, setLockouts] = useState("");
  const [hibpRejected, setHibpRejected] = useState("");
  const [ackTtl, setAckTtl] = useState("");
  const [dedupe, setDedupe] = useState("");
  const [smsRail, setSmsRail] = useState("toll_free");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/notification-preferences");
      setData(r.data);
      // Seed local state from server truth — every field reflects DB value
      // when override present, else env default. Hard-coded "" for fields
      // not explicitly in DB so user can see the placeholder.
      const src = r.data.source || {};
      setEmails((r.data.ops_alert_emails || []).join(", "));
      setFailedCreds(src.failed_creds_per_hour === "db" ? String(r.data.failed_creds_per_hour) : "");
      setLockouts(src.lockouts === "db" ? String(r.data.lockouts) : "");
      setHibpRejected(src.hibp_rejected === "db" ? String(r.data.hibp_rejected) : "");
      setAckTtl(src.ack_ttl_hours === "db" ? String(r.data.ack_ttl_hours) : "");
      setDedupe(src.dedupe_hours === "db" ? String(r.data.dedupe_hours) : "");
      setSmsRail(r.data.sms_rail || "toll_free");
      setError(null);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const parsedEmails = emails
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
      const payload = {
        ops_alert_emails: parsedEmails,
        sms_rail: smsRail,
      };
      // Only include numeric overrides if the user typed a value; empty
      // means revert-to-env, which we communicate by NOT sending the field.
      // (The PUT endpoint treats `null` as "don't touch this field".)
      const numFields = [
        ["failed_creds_per_hour", failedCreds],
        ["lockouts", lockouts],
        ["hibp_rejected", hibpRejected],
        ["ack_ttl_hours", ackTtl],
        ["dedupe_hours", dedupe],
      ];
      for (const [k, v] of numFields) {
        if (v && !isNaN(Number(v)) && Number(v) > 0) {
          payload[k] = Number(v);
        }
      }
      const r = await api.put("/admin/notification-preferences", payload);
      setData(r.data);
      setSuccess("Preferences saved. Changes apply to the next anomaly evaluation tick (~15 min).");
      setTimeout(() => setSuccess(null), 6000);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    setTesting(true);
    setError(null);
    setSuccess(null);
    try {
      const r = await api.post("/admin/notification-preferences/test");
      const count = r.data.sent_to_count || 0;
      if (r.data.ok) {
        setSuccess(`Test alert email sent to ${count} recipient${count === 1 ? "" : "s"}.`);
      } else {
        setError(`Send failed: ${r.data.error || "unknown error"}`);
      }
      setTimeout(() => setSuccess(null), 8000);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto p-6">
        <p className="text-sm text-[#4A5C50]">Loading preferences…</p>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto p-6" data-testid="notification-preferences-page">
      <Link
        to="/admin"
        className="inline-flex items-center gap-1.5 text-sm text-[#4A5C50] hover:text-[#184A30] mb-4"
        data-testid="back-to-admin"
      >
        <ArrowLeft size={14} weight="bold" /> Admin overview
      </Link>

      <div className="flex items-start gap-3 mb-6">
        <div className="p-2.5 rounded-xl bg-[#184A30] text-[#FBF9F6]">
          <Bell size={20} weight="fill" />
        </div>
        <div>
          <h1 className="text-3xl font-serif text-[#112318] leading-tight">
            Notification preferences
          </h1>
          <p className="text-sm text-[#4A5C50] mt-1 max-w-prose">
            Configure who receives ops-alert emails and tune the anomaly thresholds
            without redeploying. DB values override the env-var fallbacks.
          </p>
        </div>
      </div>

      {error && (
        <div
          className="mb-4 rounded-xl border border-rose-300 bg-rose-50 p-3 flex items-start gap-2"
          data-testid="error-banner"
        >
          <Warning size={16} weight="fill" className="text-rose-600 shrink-0 mt-0.5" />
          <p className="text-sm text-rose-900">{error}</p>
        </div>
      )}
      {success && (
        <div
          className="mb-4 rounded-xl border border-emerald-300 bg-emerald-50 p-3 flex items-start gap-2"
          data-testid="success-banner"
        >
          <CheckCircle size={16} weight="fill" className="text-emerald-700 shrink-0 mt-0.5" />
          <p className="text-sm text-emerald-900">{success}</p>
        </div>
      )}

      {/* Recipients */}
      <section
        className="mb-6 rounded-2xl border border-[#E5E0D8] bg-white p-5"
        data-testid="section-recipients"
      >
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-base font-semibold text-[#112318]">
            Ops alert recipients
          </h2>
          <SourceBadge source={data?.source?.ops_alert_emails} />
        </div>
        <p className="text-xs text-[#4A5C50] mb-3">
          Comma-separated list of emails. When this is empty, the worker falls back
          to <code className="bg-[#FBF9F6] px-1 rounded">OPS_ALERT_EMAILS</code> env, then
          to every <code className="bg-[#FBF9F6] px-1 rounded">role=admin</code> user.
        </p>
        <textarea
          value={emails}
          onChange={(e) => setEmails(e.target.value)}
          placeholder="ops@zcardon.com, alex@zcardon.com"
          rows={2}
          className="w-full rounded-lg border border-[#E5E0D8] bg-[#FBF9F6] px-3 py-2 text-sm text-[#112318] font-mono focus:outline-none focus:border-[#184A30]"
          data-testid="input-emails"
        />
        <p className="text-[11px] text-[#4A5C50] mt-2">
          Env fallback:{" "}
          <code className="font-mono">
            {(data?.env_defaults?.ops_alert_emails || []).join(", ") || "(none — uses admin users)"}
          </code>
        </p>
      </section>

      {/* Thresholds */}
      <section
        className="mb-6 rounded-2xl border border-[#E5E0D8] bg-white p-5"
        data-testid="section-thresholds"
      >
        <h2 className="text-base font-semibold text-[#112318] mb-1">
          Anomaly thresholds
        </h2>
        <p className="text-xs text-[#4A5C50] mb-4">
          The digest banner + ops email fire when any of these rules trip in the rolling 1-hour window.
          Leave a field empty to use the env-var default.
        </p>

        <ThresholdRow
          label="Failed-credentials per hour"
          hint="≥ this many failed logins in 1h triggers a brute-force alert."
          value={failedCreds}
          setValue={setFailedCreds}
          placeholder={String(data?.env_defaults?.failed_creds_per_hour ?? "")}
          source={data?.source?.failed_creds_per_hour}
          effective={data?.failed_creds_per_hour}
          testid="threshold-failed-creds"
        />
        <ThresholdRow
          label="Account lockouts"
          hint="≥ this many account lockouts in 1h triggers an alert."
          value={lockouts}
          setValue={setLockouts}
          placeholder={String(data?.env_defaults?.lockouts ?? "")}
          source={data?.source?.lockouts}
          effective={data?.lockouts}
          testid="threshold-lockouts"
        />
        <ThresholdRow
          label="Breached-password attempts (HIBP)"
          hint="≥ this many HIBP-rejected attempts in 1h triggers an alert."
          value={hibpRejected}
          setValue={setHibpRejected}
          placeholder={String(data?.env_defaults?.hibp_rejected ?? "")}
          source={data?.source?.hibp_rejected}
          effective={data?.hibp_rejected}
          testid="threshold-hibp"
        />
      </section>

      {/* Timing */}
      <section
        className="mb-6 rounded-2xl border border-[#E5E0D8] bg-white p-5"
        data-testid="section-timing"
      >
        <h2 className="text-base font-semibold text-[#112318] mb-1">
          Timing
        </h2>
        <p className="text-xs text-[#4A5C50] mb-4">
          Controls how long acknowledgments silence an alert + the worker's email-dedupe window.
        </p>

        <ThresholdRow
          label="Acknowledge TTL (hours)"
          hint="An acknowledged alert is silenced for this many hours before re-arming."
          value={ackTtl}
          setValue={setAckTtl}
          placeholder={String(data?.env_defaults?.ack_ttl_hours ?? "")}
          source={data?.source?.ack_ttl_hours}
          effective={data?.ack_ttl_hours}
          testid="threshold-ack-ttl"
        />
        <ThresholdRow
          label="Email dedupe window (hours)"
          hint="Max 1 ops email per rule per this window. Keeps inboxes clean during incidents."
          value={dedupe}
          setValue={setDedupe}
          placeholder={String(data?.env_defaults?.dedupe_hours ?? "")}
          source={data?.source?.dedupe_hours}
          effective={data?.dedupe_hours}
          testid="threshold-dedupe"
        />
      </section>

      {/* SMS Rail */}
      <section
        className="mb-6 rounded-2xl border border-[#E5E0D8] bg-white p-5"
        data-testid="section-sms-rail"
      >
        <div className="flex items-center justify-between mb-1">
          <h2 className="text-base font-semibold text-[#112318]">
            SMS rail
          </h2>
          <SourceBadge source={data?.source?.sms_rail} />
        </div>
        <p className="text-xs text-[#4A5C50] mb-3">
          Which Twilio messaging service to route outbound SMS through. Use{" "}
          <strong>toll_free</strong> for the standard ~1 msg/s registered toll-free
          number. Switch to <strong>10dlc</strong> after your 10DLC Brand + Campaign
          is approved in Twilio Console. Requires{" "}
          <code className="bg-[#FBF9F6] px-1 rounded">TWILIO_10DLC_MESSAGING_SERVICE_SID</code>{" "}
          or <code className="bg-[#FBF9F6] px-1 rounded">TWILIO_10DLC_PHONE_NUMBER</code> env
          when 10dlc is selected; if neither is set, the worker logs a warning and
          auto-falls-back to toll-free.
        </p>
        <div className="flex gap-2" role="radiogroup" data-testid="sms-rail-options">
          {["toll_free", "10dlc"].map((opt) => {
            const active = smsRail === opt;
            return (
              <button
                key={opt}
                onClick={() => setSmsRail(opt)}
                role="radio"
                aria-checked={active}
                className={`flex-1 rounded-xl border px-4 py-3 text-left transition-all ${
                  active
                    ? "border-[#184A30] bg-[#184A30] text-white shadow-sm"
                    : "border-[#E5E0D8] bg-white text-[#112318] hover:bg-[#FBF9F6]"
                }`}
                data-testid={`sms-rail-${opt}`}
              >
                <p className="text-sm font-semibold uppercase tracking-wider">
                  {opt === "toll_free" ? "Toll-free" : "10DLC"}
                </p>
                <p className={`text-[11px] mt-1 ${active ? "text-white/80" : "text-[#4A5C50]"}`}>
                  {opt === "toll_free"
                    ? "Default. Registered TF number. Subject to TFV review."
                    : "Long code. After Brand + Campaign approval."}
                </p>
              </button>
            );
          })}
        </div>
        <p className="text-[10px] text-[#4A5C50] mt-2 italic">
          Effective:{" "}
          <code className="font-mono not-italic">{data?.sms_rail}</code>
        </p>
      </section>

      {/* Actions */}
      <div className="flex flex-wrap items-center gap-3">
        <button
          onClick={save}
          disabled={saving}
          className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 bg-[#184A30] text-[#FBF9F6] text-sm font-semibold hover:bg-[#0f3a24] disabled:opacity-60 transition-colors"
          data-testid="save-preferences-btn"
        >
          {saving ? (
            <ArrowsClockwise size={14} weight="bold" className="animate-spin" />
          ) : (
            <CheckCircle size={14} weight="fill" />
          )}
          {saving ? "Saving…" : "Save preferences"}
        </button>
        <button
          onClick={sendTest}
          disabled={testing}
          className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 bg-white border border-[#184A30] text-[#184A30] text-sm font-semibold hover:bg-[#FBF9F6] disabled:opacity-60 transition-colors"
          data-testid="send-test-btn"
          title="Fire a sample ops-alert email to the current recipient list"
        >
          {testing ? (
            <ArrowsClockwise size={14} weight="bold" className="animate-spin" />
          ) : (
            <PaperPlaneTilt size={14} weight="fill" />
          )}
          {testing ? "Sending…" : "Send test alert"}
        </button>
        <button
          onClick={load}
          className="inline-flex items-center gap-1.5 text-sm text-[#4A5C50] hover:text-[#184A30]"
          data-testid="refresh-btn"
        >
          <ArrowsClockwise size={12} weight="bold" />
          Reload
        </button>
      </div>
    </div>
  );
}

function ThresholdRow({ label, hint, value, setValue, placeholder, source, effective, testid }) {
  return (
    <div className="mb-4 last:mb-0" data-testid={testid}>
      <div className="flex items-center justify-between mb-1">
        <label className="text-sm font-medium text-[#112318]">{label}</label>
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-[#4A5C50]">Effective: <code className="font-mono">{effective}</code></span>
          <SourceBadge source={source} />
        </div>
      </div>
      <input
        type="number"
        min="1"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder={`Default: ${placeholder}`}
        className="w-full rounded-lg border border-[#E5E0D8] bg-[#FBF9F6] px-3 py-2 text-sm text-[#112318] font-mono focus:outline-none focus:border-[#184A30]"
        data-testid={`${testid}-input`}
      />
      <p className="text-[11px] text-[#4A5C50] mt-1">{hint}</p>
    </div>
  );
}
